import { AxiosError } from "axios";
import axios from "./axios";

export type AzureAdRole = {
  azureAdGroupId: string;
  azureAdGroupName: string;
};

export type AuthUser = {
  id: string;
  userName: string;
  email: string;
  emailConfirmed: boolean;
  lockoutEnabled: boolean;
  isAzureAdUser: boolean;
  azureAdRoles: AzureAdRole[];
};

export type Impersonator = {
  id: string;
  name: string;
};

export type TestProfile = {
  jobTitle: string | null;
  locationCode: string | null;
  locationName: string | null;
};

export type TestProfileOptions = {
  jobTitles: string[];
  locations: { code: string; name: string }[];
};

export type AuthUserResult = {
  id: string;
  userName: string;
  email: string;
  emailConfirmed: boolean;
  lockoutEnabled: boolean;
  isAdmin: boolean;
  roles: string[];
  isAzureAdUser: boolean;
  azureAdRoles: AzureAdRole[];
  isImpersonating: boolean;
  impersonator: Impersonator | null;
  testProfile: TestProfile | null;
};

export type AuthApi = {
  authenticated(): Promise<AuthUserResult | null>;
  login(
    userName: string,
    password: string,
    rememberMe: boolean,
  ): Promise<AuthUserResult | null>;
  loginWithAzureAd(): void;
  logout(): Promise<void>;
  impersonate(userId: string): Promise<AuthUserResult>;
  stopImpersonation(): Promise<AuthUserResult>;
  getTestProfileOptions(): Promise<TestProfileOptions>;
  setTestProfile(profile: {
    jobTitle: string | null;
    locationCode: string | null;
  }): Promise<AuthUserResult>;
  forgot(userName: string): Promise<void>;
  reset(
    userName: string,
    token: string,
    password: string,
  ): Promise<string | boolean>;
};

export const authApi: AuthApi = {
  loginWithAzureAd() {
    window.location.href = "/api/v0/authentication/login/azure-ad";
  },
  async authenticated() {
    try {
      const { data: user } = await axios.get<AuthUserResult>(
        "authentication/isauthorized",
      );
      return user;
    } catch (ex: any) {
      if (ex instanceof AxiosError && ex.response?.status === 403) {
        return null;
      } else {
        throw ex;
      }
    }
  },
  async login(userName, password, rememberMe) {
    try {
      const { data: user } = await axios.post<AuthUserResult>(
        "authentication/login",
        {
          userName,
          password,
          rememberMe,
        },
      );
      return user;
    } catch (ex: any) {
      if (ex instanceof AxiosError && ex.response?.status === 400) {
        return null;
      } else {
        throw ex;
      }
    }
  },
  async logout() {
    await axios.post("authentication/logout");
  },
  async impersonate(userId) {
    const { data: user } = await axios.post<AuthUserResult>(
      `authentication/impersonate/${userId}`,
    );
    return user;
  },
  async stopImpersonation() {
    const { data: user } = await axios.post<AuthUserResult>(
      "authentication/stop-impersonation",
    );
    return user;
  },
  async getTestProfileOptions() {
    const { data } = await axios.get<TestProfileOptions>(
      "authentication/impersonation/test-profile-options",
    );
    return data;
  },
  async setTestProfile(profile) {
    const { data: user } = await axios.put<AuthUserResult>(
      "authentication/impersonation/test-profile",
      profile,
    );
    return user;
  },
  async forgot(userName) {
    await axios.post("authentication/forgot", { userName });
  },
  async reset(userId, token, password) {
    try {
      return await axios.post("authentication/reset", {
        userId,
        token,
        password,
      });
    } catch (ex: any) {
      if (ex instanceof AxiosError && ex.response?.status === 400) {
        return ex.response.data;
      } else {
        throw ex;
      }
    }
  },
};
